import { useMutation } from '@apollo/client';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { ModalContent } from '@synoptic/ui-kit/modal/modal.js';
import { ModalHeader, ModalTitle } from '@synoptic/ui-kit/modal/modal.tsx';
import { Textarea } from '@synoptic/ui-kit/textarea/textarea.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import { borderRadius, space } from '@synoptic/ui-kit/tokens.ts';
import { typography } from '@synoptic/ui-kit/typography.ts';
import { SubmitHandler, useForm } from 'react-hook-form';
import { gql } from '@/__generated__';
import { StreamState } from '@/__generated__/graphql.ts';
import { streamFormActions } from '@/components/streams/stream-form/stream-form.css.ts';

const HoldStreamMutation = gql(`
mutation HoldStream($streamId: ID!, $input: HoldStreamInput!) {
  holdStream(id: $streamId, input: $input) {
    id
    streamState
  }
}
`);

export const HoldStreamModal = ({
  onOpenChange,
  streamId,
}: {
  onOpenChange: (open: boolean) => void;
  streamId: string;
}) => {
  const toast = useToast();

  const { handleSubmit, formState, register } = useForm<{
    description: string;
  }>();

  const [holdStream] = useMutation(HoldStreamMutation, {
    onError(error) {
      toast.error({
        title: 'Failed to hold stream',
        subTitle: error.message,
      });
    },
  });

  const submit: SubmitHandler<{ description: string }> = async (data) => {
    holdStream({
      variables: {
        streamId,
        input: { description: data.description },
      },
      optimisticResponse: {
        holdStream: {
          __typename: 'Stream',
          id: streamId,
          streamState: StreamState.OnHold,
        },
      },
    });

    onOpenChange(false);
  };

  return (
    <ModalContent>
      <ModalHeader>
        <ModalTitle>Hold stream</ModalTitle>
      </ModalHeader>

      <form onSubmit={handleSubmit(submit)}>
        <Textarea
          {...register('description', {
            required: 'Description is required',
            maxLength: {
              value: 20000,
              message: 'Description cannot exceed 20000 characters',
            },
            validate: (v) => (v ? v.trim().length > 0 : undefined),
          })}
          errorMessage={formState.errors.description?.message}
          placeholder={'What is the reason for holding the stream?'}
          style={{
            ...typography.bodyMd,
            padding: `${space.s12}px ${space.s16}px`,
            borderRadius: borderRadius.r8,
            minHeight: 100,
          }}
        />

        <div className={streamFormActions}>
          <Button
            disabled={formState.isSubmitting}
            type="button"
            variant={'secondary'}
            size={'medium'}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>

          <Button
            disabled={!formState.isValid}
            type="submit"
            size={'medium'}
            loading={formState.isSubmitting}
          >
            Hold now
          </Button>
        </div>
      </form>
    </ModalContent>
  );
};
